/**
 * usePackPurchase — fluxo de compra de pacote por PIX, compartilhado entre Integrações e /billing.
 *
 * Desde 10/10/2026 (docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md, TODO 3.4): comprar abre SEMPRE a
 * modal "Dados para o pagamento" (CPF ou CNPJ + nome ou razão social), já preenchida com o que está
 * salvo. Saiu o "400 abre o diálogo de CPF": qualquer 400 abria o diálogo, e só aceitava 11 dígitos —
 * empresa com CNPJ não comprava (Funpresp-Jud, 09/10).
 *
 * Uso:
 *   const compra = usePackPurchase({ onPaid, showError, showSuccess });
 *   compra.handleBuy(produto)     // comprar: abre a modal de dados
 *   compra.handleResume(produto)  // retomar PIX pendente: SEM modal (é o mesmo PIX)
 *   <DadosDeCobrancaDialog {...compra.cobranca} />
 *   {compra.checkoutOrder && <PixQrCheckout order={compra.checkoutOrder} onClose={compra.closeCheckout} />}
 */
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import billingService from '../services/billingService';
import useDadosDeCobranca from './useDadosDeCobranca';

export default function usePackPurchase({ onPaid, showError, showSuccess } = {}) {
  const queryClient = useQueryClient();
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const cobranca = useDadosDeCobranca();

  // A API é quem garante "uma compra por pacote": abrir de novo devolve o MESMO PIX (reused) e, se o
  // pagamento já entrou — inclusive por outra máquina —, responde alreadyPaid em vez de um QR novo.
  // A tela nunca decide isso sozinha (17/09/2026: dois PIX do mesmo pacote, um pago e outro pagável).
  const openCheckout = useCallback((order) => {
    if (order?.alreadyPaid) {
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      onPaid?.();
      showSuccess?.('Este pacote já foi pago. Os créditos estão na sua conta.');
      return;
    }
    setCheckoutOrder(order);
  }, [queryClient, onPaid, showSuccess]);

  // Sem providerSlug: escolher adquirente é do Billing (ele vê taxa de sucesso e quem caiu).
  // Mandar o slug daqui vazava o adquirente para o front e impedia o Billing de desviar.
  const comprarComDados = useCallback(async (product, { document, billingName }) => {
    try {
      const order = await billingService.createOrder({
        kind: 'pack', productId: product.id, methods: ['Pix'], document, billingName,
      });
      openCheckout(order);
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        erro: err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Tente novamente.',
      };
    }
  }, [openCheckout]);

  const { abrir } = cobranca;
  const handleBuy = useCallback((product) => {
    if (!product) { showError?.('Não foi possível iniciar o pagamento. Tente novamente.'); return; }
    abrir((dados) => comprarComDados(product, dados));
  }, [abrir, comprarComDados, showError]);

  // RETOMAR um PIX pendente NÃO abre a modal: a API devolve o MESMO PIX (com os dados com que ele
  // foi criado) e ignoraria o que fosse digitado — pedir os dados ali seria atrito e daria a falsa
  // impressão de trocar o documento do PIX. Só se a API recusar por falta de documento (o PIX venceu
  // e não há nada salvo) é que a modal abre.
  const handleResume = useCallback(async (product) => {
    if (!product) { showError?.('Não foi possível iniciar o pagamento. Tente novamente.'); return; }
    try {
      openCheckout(await billingService.createOrder({ kind: 'pack', productId: product.id, methods: ['Pix'] }));
    } catch (err) {
      if (err?.response?.status === 400) abrir((dados) => comprarComDados(product, dados));
      else showError?.(err?.response?.data?.message || 'Não foi possível retomar o pagamento. Tente novamente.');
    }
  }, [abrir, comprarComDados, openCheckout, showError]);

  // Ao fechar o checkout, quem chamou recarrega o que precisa (o pagamento pode ter concedido lote novo).
  const closeCheckout = useCallback(() => { setCheckoutOrder(null); onPaid?.(); }, [onPaid]);

  return {
    checkoutOrder,
    closeCheckout,
    handleBuy,
    handleResume,
    cobranca: { vm: cobranca.vm, actions: cobranca.actions },
  };
}
